import React, { useContext } from "react";
import { useHistory } from "react-router";
import { RESOURCE_NAME } from "common/authorization";
import {
  buildLink,
  INFO_PANEL_TAB_NAME,
  RECORDS_TAB_NAME,
} from "common/routing";
import { useDeleteInfoRequestLogMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { AuthContext } from "../Authorization/AuthContext";
import { ActionCell } from "../Common/ActionCell";
import { getDefaultDeleteBody, useDeleteModal } from "../Common/DeleteModal";
import {
  ActionsProps,
  shouldShowViewOnMapActionForProperty,
} from "../Inputs/DropdownMenu";
import { InformationRequestFromList } from "./queries";

export const ActionButton = ({
  isDetailView,
  infoRequest,
  prevLocation,
  onUpdate,
}: {
  infoRequest: {
    id: string;
    property?: Maybe<{
      fullAddress?: Maybe<string>;
      id: string;
      latitude?: Maybe<number>;
      longitude?: Maybe<number>;
    }>;
  };
  isDetailView: boolean;
  prevLocation?: string;
  onUpdate?: () => void;
}) => {
  const { authorized } = useContext(AuthContext);
  const history = useHistory();
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [deleteLog] = useDeleteInfoRequestLogMutation({
    onCompleted: data => {
      if (data.deleteInformationRequest) {
        onUpdate?.();
        addSuccessToast("Log entry was successfully deleted");

        history.push(prevLocation ?? "/map");
      } else {
        addErrorToast(
          "There was an issue deleting this log. Please try again. If the problem persists, please email us at support@withforerunner.com",
        );
      }
    },
    onError: () => {
      hideDeleteLogModal();
      addErrorToast(
        "There was an issue deleting this log. Please try again. If the problem persists, please email us at support@withforerunner.com",
      );
    },
    update: cache => {
      // please see this GH issue as to why we're writing the cache this way:
      // https://github.com/apollographql/apollo-client/issues/6451
      cache.modify({
        fields: {
          informationRequests(
            requests: Array<InformationRequestFromList>,
            { readField },
          ) {
            return requests.filter(
              request => readField("id", request) !== infoRequest.id,
            );
          },
        },
      });
    },
  });

  const onDelete = async () => {
    await deleteLog({ variables: { id: infoRequest.id } });
  };

  const [showDeleteLogModal, hideDeleteLogModal] = useDeleteModal({
    size: "small",
    headerText: "Delete log",
    Content: getDefaultDeleteBody("log"),
    deleteButtonText: "Delete",
    onDelete,
  });

  const canDeleteLog = authorized({
    resource: RESOURCE_NAME.INFORMATION_REQUEST,
    permission: "delete",
  });

  const actions: Array<ActionsProps> = [];
  if (!isDetailView) {
    actions.push({
      label: "View",
      href: buildLink("editLog", { id: infoRequest.id }, {}),
      target: "_blank",
    });
  }

  const property = infoRequest.property;
  if (shouldShowViewOnMapActionForProperty(property)) {
    actions.push({
      label: "View on map",
      href: buildLink(
        "map",
        {},
        {
          propertyId: property.id,
          lat: property.latitude.toString(),
          lng: property.longitude.toString(),
          address: property.fullAddress,
          tab: INFO_PANEL_TAB_NAME.RECORDS,
          recordTab: RECORDS_TAB_NAME.LOGS,
        },
      ),
      target: "_blank",
    });
  }
  actions.push({
    label: "Delete",
    variant: "red",
    onClick: () => {
      showDeleteLogModal();
    },
    closeOnClick: false,
    disabled: !canDeleteLog,
  });

  return (
    <ActionCell
      actions={actions}
      buttonText={isDetailView ? "Actions" : undefined}
    />
  );
};
